import { isValidPhoneNumber } from 'libphonenumber-js';
import { useAuthUser } from 'components/shared/hooks/useAuthUser';
import { useEffect, useMemo, useCallback } from 'react';
import { useTwilio } from 'providers/TwilioProvider/hooks/public/useTwilio';
import parsePhoneNumber from 'libphonenumber-js';

/**
 * Helper to get messages for a conversation SID and intitialize the conversations client for the selected conversation.
 * @param {string} conversationSID
 * @returns {{
 *   loading: boolean,
 *   messages: Message[][] | null - An array of batched message arrays organized chronologically and grouped by whether they are inbound or outbound
 * }}
 */
export const useMessagesForConversationSID = conversationSID => {
  const {
    getMessagesForConversationSID,
    messagesByConversationSID,
    identity,
    conversationsClient,
    handleConversationJoined,
    handleConversationLeft,
    handleConversationUpdated,
    bindMessageAddedEventListener,
    ensureConversationParticipants,
  } = useTwilio();

  const user = useAuthUser();

  useEffect(() => {
    const requiredDataNotYetPresent =
      !conversationSID || !messagesByConversationSID;

    const messagesWereAlreadyLoaded =
      conversationSID in messagesByConversationSID;

    if (requiredDataNotYetPresent || messagesWereAlreadyLoaded) return;

    getMessagesForConversationSID(conversationSID);
  }, [conversationSID, messagesByConversationSID]);

  /**
   * Determine if this message was sent by the current user.
   * if the author matches, then yes. Otherwise, if it is a valid phone number
   * then that was sent by the client
   *
   * @returns {boolean}
   */
  const isOutgoingMessage = useCallback(
    message => {
      if (!user) return false;
      const authUserPhone = parsePhoneNumber(user.phoneNumber, 'US');

      const messageAuthorIsLoggedInUser =
        message.author === identity || message.author === authUserPhone?.number;
      const participantIsAClientUsingSMS = isValidPhoneNumber(
        message.author,
        'US'
      );

      return (
        !messageAuthorIsLoggedInUser ||
        (participantIsAClientUsingSMS && user.role !== 'customer')
      );
    },
    [identity, user]
  );

  const messages = useMemo(() => {
    if (
      !user ||
      !conversationSID ||
      !messagesByConversationSID ||
      !messagesByConversationSID[conversationSID]
    )
      return null;

    /**
     * If the previous message type is not the same as the current message type,
     * create a new batch. Otherwise, add the current message to the current batch.
     */
    const reducer = (batches, message, index, array) => {
      const currentMessageTypeIsOutgoing = isOutgoingMessage(message);

      batches.push({
        ...message.state,
        isOutgoing: currentMessageTypeIsOutgoing,
        isFirstMessageInParticipantBatch:
          index === 0 ||
          isOutgoingMessage(message) !== isOutgoingMessage(array[index - 1]),
        isLastMessageInParticipantBatch:
          index === array.length - 1 ||
          isOutgoingMessage(message) !== isOutgoingMessage(array[index + 1]),
      });

      return batches;
    };

    return messagesByConversationSID[conversationSID].messages?.reduce(
      reducer,
      []
    );
  }, [conversationSID, messagesByConversationSID, user]);

  const loading = useMemo(() => {
    if (!conversationSID || !messagesByConversationSID) return false;

    return messagesByConversationSID[conversationSID]?.loading;
  }, [messagesByConversationSID, conversationSID]);

  const refetch = useCallback(
    async () => await getMessagesForConversationSID(conversationSID),
    [getMessagesForConversationSID, conversationSID]
  );

  const initializeAndBindConversationClient = useCallback(
    async conversation => {
      if (conversation.source === 'server' && conversationsClient) {
        if (conversation.numParticipants < 2) {
          await ensureConversationParticipants(conversation);
        }
        const activeConversation =
          await conversationsClient.getConversationBySid(conversation.sid);
        activeConversation.on('conversationJoined', handleConversationJoined);
        activeConversation.on('conversationLeft', handleConversationLeft);
        activeConversation.on('conversationUpdated', handleConversationUpdated);

        await bindMessageAddedEventListener(activeConversation);
        await getMessagesForConversationSID(activeConversation.sid);
      }
    },
    [conversationsClient]
  );

  return {
    loading,
    messages,
    refetch,
    isOutgoingMessage,
    initializeAndBindConversationClient,
    ensureConversationParticipants,
  };
};
